import { ChevronDown, AlertCircle } from 'lucide-react';
import { useState } from 'react';

interface MobileFunnelCardProps {
  name: string;
  opportunities: number;
  avgValue: string;
  totalValue: string;
  probability: number;
  atRisk: number;
  riskLevel: 'low' | 'medium' | 'high';
  conversionRate: number;
  stageIndex?: number;
  weightedValue: string;
  avgDays: number;
  agingDeals: number;
  onCardClick: () => void;
  onAtRiskClick: () => void;
}

export function MobileFunnelCard({
  name,
  opportunities,
  avgValue,
  totalValue,
  probability,
  atRisk,
  riskLevel,
  conversionRate,
  stageIndex = 0,
  weightedValue,
  avgDays,
  agingDeals,
  onCardClick,
  onAtRiskClick,
}: MobileFunnelCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const getRiskColors = () => {
    switch (riskLevel) {
      case 'high':
        return { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', hover: 'hover:bg-rose-100' };
      case 'medium':
        return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', hover: 'hover:bg-amber-100' };
      case 'low':
        return { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', hover: 'hover:bg-emerald-100' };
    }
  };

  // Get gradient background (subtle teal gradients - matching desktop)
  const getGradientClass = () => {
    const gradients = [
      'from-[#E8F4F6] to-white', // Lightest teal
      'from-[#D0E9ED] to-white', 
      'from-[#B8DEE4] to-white',
      'from-[#A0D3DB] to-white', // Slightly darker
    ];
    return `bg-gradient-to-br ${gradients[stageIndex] || 'from-white to-white'}`;
  };

  const riskColors = getRiskColors();

  return (
    <div className={`rounded-lg border border-gray-200 ${getGradientClass()}`}>
      {/* Card Header - Always Visible */}
      <div className="w-full p-2">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full hover:opacity-80 transition-opacity"
        >
          {/* Stage Name Header with At-Risk */}
          <div className="flex items-center gap-1.5 mb-1.5">
            <ChevronDown
              className={`w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0 ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
            <h3 className="text-gray-900 flex-1 text-left text-sm">{name}</h3>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-400">{probability}%</span>
              {atRisk > 0 && (
                <div 
                  className={`inline-flex items-center gap-0.5 ${riskColors.bg} ${riskColors.text} text-xs px-1 py-0.5 rounded flex-shrink-0`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAtRiskClick();
                  }}
                >
                  <AlertCircle className="w-3 h-3" />
                  <span>{atRisk} at risk</span>
                </div>
              )}
            </div>
          </div>

          {/* Metrics Grid - Ultra Compact */}
          <div className="grid grid-cols-2 gap-1.5">
            {/* Opportunities */}
            <div className="text-center">
              <p className="text-lg text-gray-900">{opportunities}</p>
              <p className="text-xs text-gray-400">opps</p>
            </div>
            
            {/* Total Value */}
            <div className="text-center">
              <p className="text-lg text-gray-900">{totalValue}</p>
              <p className="text-xs text-gray-400">total</p>
            </div>
          </div>
        </button>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-gray-100 pt-2 mt-1.5 space-y-2">
            {/* 2-Column Metrics Layout */}
            <div className="grid grid-cols-2 gap-3">
              {/* Deal Metrics Section */}
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wide text-gray-400">Deal Metrics</p>
                <div className="space-y-1">
                  <p className="text-xs text-gray-600">Avg Size: {avgValue}</p>
                  <div>
                    <p className="text-xs text-gray-600">Weighted: {weightedValue}</p>
                    <p className="text-[10px] text-gray-400">(realistic forecast)</p>
                  </div>
                </div>
              </div>

              {/* Velocity Section */}
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wide text-gray-400">Velocity</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-xs text-gray-600">
                    <span 
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        avgDays <= 30 ? 'bg-[#10b981]' : 
                        avgDays <= 60 ? 'bg-[#f59e0b]' : 
                        'bg-[#ef4444]'
                      }`}
                    />
                    <span>Avg Days: {avgDays}d</span>
                  </div>
                  {agingDeals > 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-[#ef4444]">
                      <span className="w-2 h-2 rounded-full bg-[#ef4444] flex-shrink-0" />
                      <span>Aging: {agingDeals} deals &gt;60d</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <button
              onClick={onCardClick}
              className="w-full bg-[#E8F4F6] text-[#4A90A4] py-1.5 rounded text-xs hover:bg-[#D0E9ED] transition-colors border border-[#4A90A4]/20 text-center"
            >
              View All Opportunities
            </button>
          </div>
        )}
      </div>
      
      {/* Conversion Rate Divider - Between Cards */}
      <div className="border-t border-[#4A90A4]/20 bg-white/50 py-0.5 px-2 flex items-center justify-center">
        <span className="text-xs text-[#4A90A4]">{conversionRate}% →</span>
      </div>
    </div>
  );
}
